import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Wine, Users, Mail, CalendarDays, MessageSquare, LogOut, Plus, Trash2, Pencil, X, Send,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const TABS = [
  { id: "members", label: "Members", icon: Users },
  { id: "events", label: "Gatherings", icon: CalendarDays },
  { id: "rsvps", label: "RSVPs", icon: Mail },
  { id: "suggestions", label: "Ideas", icon: MessageSquare },
  { id: "newsletter", label: "Newsletter", icon: Mail },
];

const EMPTY_EVENT = { title: "", description: "", date: "", time: "", location: "", image: "", capacity: 0, published: true, reminder_message: "" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("members");
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [eventForm, setEventForm] = useState(null);
  const [mail, setMail] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/admin/rsvps").then((r) => setRsvps(r.data)).catch(() => {});
    api.get("/admin/suggestions").then((r) => setSuggestions(r.data)).catch(() => {});
    api.get("/admin/newsletter").then((r) => setNewsletter(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) loadAll(); }, [user]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const saveEvent = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...eventForm, capacity: Number(eventForm.capacity) || 0 };
      if (eventForm.id) await api.put(`/admin/events/${eventForm.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success("Gathering saved");
      setEventForm(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this gathering and its RSVPs?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const deleteSuggestion = async (id) => {
    if (!window.confirm("Delete this idea?")) return;
    await api.delete(`/admin/suggestions/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const sendNewsletter = async (test) => {
    if (!mail.subject.trim() || !mail.message.trim()) {
      toast.error("Add a subject and a message first.");
      return;
    }
    if (!test && !window.confirm(`Send this newsletter to all ${newsletter.length} subscriber(s)?`)) return;
    setSending(true);
    try {
      const body = { subject: mail.subject, message: mail.message };
      if (test) body.test_email = user.email;
      const { data } = await api.post("/admin/newsletter/send", body);
      if (data.success && data.sent > 0) {
        toast.success(test ? `Test sent to ${user.email}` : `Newsletter sent to ${data.sent} subscriber(s)`);
        if (!test) setMail({ subject: "", message: "" });
      } else if (!test && data.total === 0) {
        toast.info(data.message || "No subscribers to send to yet.");
      } else {
        toast.error(data.detail || "Couldn't send right now — please try again in a minute.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return <div className="min-h-screen bg-wine-bg flex items-center justify-center text-smoke">Loading…</div>;
  }

  const statCards = stats
    ? [
        { label: "Members", value: stats.members, icon: Users },
        { label: "Newsletter", value: stats.newsletter, icon: Mail },
        { label: "Gatherings", value: stats.events, icon: CalendarDays },
        { label: "RSVPs", value: stats.rsvps, icon: Mail },
        { label: "Ideas", value: stats.suggestions, icon: MessageSquare },
      ]
    : [];

  return (
    <div className="min-h-screen bg-wine-bg text-cream">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-wine-bg/80 backdrop-blur-xl">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wine className="h-5 w-5 text-gold" />
            <span className="font-display text-xl tracking-tight">Society Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-smoke hidden sm:block">{user.email}</span>
            <button data-testid="logout-btn" onClick={doLogout} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-terracotta transition-colors">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {statCards.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} className="rounded-xl border border-white/10 bg-wine-surface p-5">
              <s.icon className="h-5 w-5 text-gold" />
              <div className="mt-3 font-display text-4xl">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.16em] text-smoke mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${
                tab === t.id ? "bg-gold text-wine-bg" : "text-smoke hover:text-cream"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Panels */}
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {tab === "members" && (
            <TableWrap testid="members-table" empty={members.length === 0} emptyText="No members yet.">
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-smoke">Name</TableHead>
                  <TableHead className="text-smoke">Email</TableHead>
                  <TableHead className="text-smoke">Phone</TableHead>
                  <TableHead className="text-smoke">Age</TableHead>
                  <TableHead className="text-smoke">Wine</TableHead>
                  <TableHead className="text-smoke">Suburb</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id} className="border-white/10 hover:bg-white/5">
                    <TableCell className="text-cream">{m.name}</TableCell>
                    <TableCell className="text-smoke">{m.email}</TableCell>
                    <TableCell className="text-smoke">{m.phone || "—"}</TableCell>
                    <TableCell className="text-smoke">{m.age_range || "—"}</TableCell>
                    <TableCell className="text-smoke">{m.wine_preference || "—"}</TableCell>
                    <TableCell className="text-smoke">{m.suburb || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableWrap>
          )}

          {tab === "events" && (
            <div>
              <div className="flex justify-end mb-4">
                <button data-testid="new-event-btn" onClick={() => setEventForm({ ...EMPTY_EVENT })} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-medium text-cream hover:bg-gold hover:text-wine-bg transition-colors">
                  <Plus className="h-4 w-4" /> New gathering
                </button>
              </div>
              <TableWrap testid="events-table" empty={events.length === 0} emptyText="No gatherings yet.">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-smoke">Title</TableHead>
                    <TableHead className="text-smoke">Date</TableHead>
                    <TableHead className="text-smoke">Location</TableHead>
                    <TableHead className="text-smoke">RSVPs</TableHead>
                    <TableHead className="text-smoke">Status</TableHead>
                    <TableHead className="text-smoke text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((ev) => (
                    <TableRow key={ev.id} className="border-white/10 hover:bg-white/5">
                      <TableCell className="text-cream max-w-xs truncate">{ev.title}</TableCell>
                      <TableCell className="text-smoke">{ev.date} {ev.time}</TableCell>
                      <TableCell className="text-smoke max-w-[200px] truncate">{ev.location}</TableCell>
                      <TableCell className="text-smoke">{ev.rsvp_count}{ev.capacity ? `/${ev.capacity}` : ""}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-1 rounded-full ${ev.published ? "bg-green-500/15 text-green-400" : "bg-white/10 text-smoke"}`}>{ev.published ? "Published" : "Draft"}</span></TableCell>
                      <TableCell className="text-right">
                        <button data-testid={`edit-event-${ev.id}`} onClick={() => setEventForm(ev)} className="p-2 hover:text-gold transition-colors"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`delete-event-${ev.id}`} onClick={() => deleteEvent(ev.id)} className="p-2 hover:text-terracotta transition-colors"><Trash2 className="h-4 w-4" /></button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </TableWrap>
            </div>
          )}

          {tab === "rsvps" && (
            <TableWrap testid="rsvps-table" empty={rsvps.length === 0} emptyText="No RSVPs yet.">
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-smoke">Name</TableHead>
                  <TableHead className="text-smoke">Email</TableHead>
                  <TableHead className="text-smoke">Gathering</TableHead>
                  <TableHead className="text-smoke">Guests</TableHead>
                  <TableHead className="text-smoke">Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rsvps.map((r) => (
                  <TableRow key={r.id} className="border-white/10 hover:bg-white/5">
                    <TableCell className="text-cream">{r.name}</TableCell>
                    <TableCell className="text-smoke">{r.email}</TableCell>
                    <TableCell className="text-smoke max-w-[220px] truncate">{r.event_title}</TableCell>
                    <TableCell className="text-smoke">{r.guests}</TableCell>
                    <TableCell className="text-smoke max-w-[220px] truncate">{r.note || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableWrap>
          )}

          {tab === "suggestions" && (
            <TableWrap testid="suggestions-table" empty={suggestions.length === 0} emptyText="No ideas posted yet.">
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-smoke">Title</TableHead>
                  <TableHead className="text-smoke">Description</TableHead>
                  <TableHead className="text-smoke">Alias</TableHead>
                  <TableHead className="text-smoke">Votes</TableHead>
                  <TableHead className="text-smoke text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suggestions.map((s) => (
                  <TableRow key={s.id} className="border-white/10 hover:bg-white/5">
                    <TableCell className="text-cream">{s.title}</TableCell>
                    <TableCell className="text-smoke max-w-sm truncate">{s.description}</TableCell>
                    <TableCell className="text-smoke">{s.alias}</TableCell>
                    <TableCell className="text-smoke">{s.votes}</TableCell>
                    <TableCell className="text-right">
                      <button data-testid={`delete-sugg-${s.id}`} onClick={() => deleteSuggestion(s.id)} className="p-2 hover:text-terracotta transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableWrap>
          )}

          {tab === "newsletter" && (
            <div className="space-y-6">
              <div data-testid="newsletter-compose" className="rounded-xl border border-white/10 bg-wine-surface p-6">
                <h3 className="font-display text-2xl text-cream">Compose newsletter</h3>
                <p className="mt-1 text-sm text-smoke">Sends to all {newsletter.length} subscriber(s). Emails come from “Macarthur Wine Society”.</p>
                <div className="mt-5 space-y-4">
                  <div>
                    <Label className="text-smoke">Subject</Label>
                    <Input data-testid="newsletter-subject" value={mail.subject} onChange={(e) => setMail({ ...mail, subject: e.target.value })} className="mt-1.5 bg-wine-bg border-white/15 text-cream" placeholder="e.g. Our first gathering is confirmed!" />
                  </div>
                  <div>
                    <Label className="text-smoke">Message</Label>
                    <Textarea data-testid="newsletter-message" rows={7} value={mail.message} onChange={(e) => setMail({ ...mail, message: e.target.value })} className="mt-1.5 bg-wine-bg border-white/15 text-cream" placeholder={"Hi everyone,\n\nWrite your update here. Line breaks are preserved.\n\nCheers,\nThe Society"} />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button data-testid="newsletter-send-test" disabled={sending} onClick={() => sendNewsletter(true)} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-cream hover:border-gold transition-colors disabled:opacity-60">
                      Send test to me
                    </button>
                    <button data-testid="newsletter-send-all" disabled={sending} onClick={() => sendNewsletter(false)} className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-wine-bg hover:bg-cream transition-colors disabled:opacity-60">
                      <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send to all subscribers"}
                    </button>
                  </div>
                </div>
              </div>

              <TableWrap testid="newsletter-table" empty={newsletter.length === 0} emptyText="No subscribers yet.">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-smoke">Email</TableHead>
                    <TableHead className="text-smoke">Subscribed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {newsletter.map((n) => (
                    <TableRow key={n.id} className="border-white/10 hover:bg-white/5">
                      <TableCell className="text-cream">{n.email}</TableCell>
                      <TableCell className="text-smoke">{new Date(n.created_at).toLocaleDateString("en-AU")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </TableWrap>
            </div>
          )}
        </motion.div>
      </div>

      {/* Event editor */}
      <Dialog open={!!eventForm} onOpenChange={(o) => !o && setEventForm(null)}>
        <DialogContent data-testid="event-dialog" className="bg-wine-surface border-white/10 text-cream sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{eventForm?.id ? "Edit gathering" : "New gathering"}</DialogTitle>
          </DialogHeader>
          {eventForm && (
            <form onSubmit={saveEvent} className="space-y-4">
              <div><Label className="text-smoke">Title</Label><Input data-testid="event-title" required value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} className="mt-1.5 bg-wine-bg border-white/15 text-cream" /></div>
              <div><Label className="text-smoke">Description</Label><Textarea data-testid="event-desc" required value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} className="mt-1.5 bg-wine-bg border-white/15 text-cream" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-smoke">Date</Label><Input data-testid="event-date" type="date" required value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} className="mt-1.5 bg-wine-bg border-white/15 text-cream" /></div>
                <div><Label className="text-smoke">Time</Label><Input data-testid="event-time" placeholder="2:00 PM" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} className="mt-1.5 bg-wine-bg border-white/15 text-cream" /></div>
              </div>
              <div><Label className="text-smoke">Location</Label><Input data-testid="event-location" required value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} className="mt-1.5 bg-wine-bg border-white/15 text-cream" /></div>
              <div><Label className="text-smoke">Image URL</Label><Input data-testid="event-image" value={eventForm.image} onChange={(e) => setEventForm({ ...eventForm, image: e.target.value })} className="mt-1.5 bg-wine-bg border-white/15 text-cream" /></div>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div><Label className="text-smoke">Capacity</Label><Input data-testid="event-capacity" type="number" min="0" value={eventForm.capacity} onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value })} className="mt-1.5 bg-wine-bg border-white/15 text-cream" /></div>
                <label className="flex items-center gap-2 text-sm text-smoke pb-3 cursor-pointer">
                  <input data-testid="event-published" type="checkbox" checked={eventForm.published} onChange={(e) => setEventForm({ ...eventForm, published: e.target.checked })} className="h-4 w-4 accent-gold" /> Published
                </label>
              </div>

              <div className="rounded-xl border border-white/10 bg-wine-bg/60 p-4">
                <Label className="text-smoke">Reminder message <span className="text-smoke/60">(auto-sent to attendees 3 days before)</span></Label>
                <Textarea
                  data-testid="event-reminder"
                  rows={4}
                  value={eventForm.reminder_message || ""}
                  onChange={(e) => setEventForm({ ...eventForm, reminder_message: e.target.value })}
                  className="mt-1.5 bg-wine-surface border-white/15 text-cream"
                  placeholder={`Just a friendly reminder — ${eventForm.title || "our gathering"} is only a few days away. We're really looking forward to seeing you there! …`}
                />
                <p className="mt-2 text-xs text-smoke/70">Leave blank to use the default friendly wording. The date and location are added automatically.</p>
                <details className="mt-3">
                  <summary data-testid="reminder-preview-toggle" className="cursor-pointer text-xs text-gold">Preview reminder</summary>
                  <div data-testid="reminder-preview" className="mt-2 rounded-lg border border-white/10 bg-wine-surface p-3 text-sm text-cream/90 leading-relaxed">
                    <p>Hi [name],</p>
                    <p className="mt-2 whitespace-pre-wrap">{(eventForm.reminder_message || "").trim() || `Just a friendly reminder — ${eventForm.title || "our gathering"} is only a few days away. We're really looking forward to seeing you there! Nothing to bring but yourself and a love of good wine. If your plans have changed, just let us know so we can free up your spot.`}</p>
                    <p className="mt-2">📅 {eventForm.date} {eventForm.time}</p>
                    <p>📍 {eventForm.location}</p>
                    <p className="mt-2">See you soon,<br/>The Macarthur Wine Society</p>
                  </div>
                </details>
              </div>

              <button data-testid="save-event-btn" className="w-full rounded-full bg-gold py-3 text-sm font-semibold text-wine-bg hover:bg-cream transition-colors">Save gathering</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TableWrap({ children, empty, emptyText, testid }) {
  if (empty) return <div data-testid={testid} className="rounded-xl border border-white/10 bg-wine-surface p-12 text-center text-smoke">{emptyText}</div>;
  return (
    <div data-testid={testid} className="rounded-xl border border-white/10 bg-wine-surface overflow-x-auto">
      <Table>{children}</Table>
    </div>
  );
}
